/**
 * Cashflow Forecast — Iter 51.3 (currency-aware + reconcile · 2026-05-18)
 *
 * Operational visibility tool with full currency segregation:
 *   - INR-only headline + monthly chart (back-compat with Iter 51)
 *   - Foreign-currency totals reported separately in native units
 *     (no FX conversion — explicit policy, Multi-Currency Phase 2 deferred)
 *   - Per-month "Reconcile" drill-down lists every contributing schedule
 *     / invoice / milestone with currency + amount + source date
 */
import { useEffect, useMemo, useState } from "react";
import {
    ChartLineUp, ArrowsClockwise, Warning, Repeat, Receipt, Coins,
    CurrencyDollar, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";
import CashflowErrorBoundary from "./CashflowErrorBoundary";


function formatINRCompact(n) {
    if (!n || n === 0) return "—";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n.toFixed(0)}`;
}

// Iter 51.3 — native-currency formatter (no FX, no INR conversion).
// Uses Intl with no fraction digits to keep the chart readable.
function formatCcy(amount, ccy) {
    if (!amount && amount !== 0) return "—";
    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: ccy,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${ccy} ${Math.round(amount).toLocaleString("en-IN")}`;
    }
}


export default function CashflowForecast() {
    // Iter 51.0.2 — page-scoped error boundary so any unexpected render
    // failure inside <CashflowForecastInner /> degrades to a visible
    // error+retry surface instead of collapsing the entire React tree
    // to a blank viewport. The boundary is local to this page (not a
    // global wrapper) per scope directive.
    return (
        <CashflowErrorBoundary>
            <CashflowForecastInner />
        </CashflowErrorBoundary>
    );
}


function CashflowForecastInner() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reconcileMonth, setReconcileMonth] = useState(null);  // ISO YYYY-MM-DD

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data: d } = await api.get("/billing/reports/cashflow-forecast");
            setData(d);
        } catch (err) {
            const msg = formatApiError(err, "Failed to load forecast");
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const maxBucket = useMemo(() => {
        if (!data?.months?.length) return 1;
        return Math.max(1, ...data.months.map((m) => m.total));
    }, [data]);

    if (loading && !data) {
        return (
            <div data-testid="forecast-page-loading">
                <PageHeader eyebrow="Cashflow Forecast" title="Loading…" />
                <div className="px-6 sm:px-8 py-8 text-sm text-slate-500">Fetching 12-month projection…</div>
            </div>
        );
    }

    // Iter 51.0.1: defensive — never collapse the page into a blank
    // `return null` when the API fails or returns an unexpected shape.
    // Render an explicit error state so the AppLayout shell stays visible
    // and the operator can retry without a hard refresh.
    if (!data || !Array.isArray(data.months)) {
        return (
            <div data-testid="cashflow-forecast-error">
                <PageHeader
                    eyebrow="Cashflow Forecast"
                    title="Expected cashflow — next 12 months"
                    actions={
                        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="forecast-retry-btn">
                            <ArrowsClockwise size={13} /> Retry
                        </Button>
                    }
                />
                <div className="px-6 sm:px-8 py-8">
                    <EmptyState
                        icon={<Warning size={36} weight="duotone" className="text-rose-500" />}
                        title="Couldn't load the cashflow projection"
                        description={
                            error
                                ? `${error}. The page will retry automatically on Refresh.`
                                : "The projection endpoint returned no data. This usually indicates a backend connectivity issue — check your network or wait a moment and retry."
                        }
                        action={
                            <Button size="sm" onClick={load} className="gap-1.5" data-testid="forecast-retry-empty">
                                <ArrowsClockwise size={13} /> Try again
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    const hl = data.headline;
    const foreignTotals = hl.foreign_currency_totals || {};
    const foreignCurrencies = Object.keys(foreignTotals).sort();
    const allZero =
        data.months.every((m) => m.total === 0)
        && hl.overdue_outstanding === 0
        && foreignCurrencies.length === 0;

    return (
        <div data-testid="cashflow-forecast-page">
            <PageHeader
                eyebrow="Cashflow Forecast"
                title={
                    <span className="flex items-center gap-3">
                        <ChartLineUp size={22} weight="duotone" className="text-emerald-600" />
                        Expected cashflow — next 12 months
                    </span>
                }
                description="AMC recurring billings (rolling commercial relationships) + one-time invoice expectations. Operational visibility only — not a predictive forecast. INR-only headline; foreign currencies reported separately in native units."
                actions={
                    <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="forecast-refresh-btn">
                        <ArrowsClockwise size={13} /> Refresh
                    </Button>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* ── Headline KPIs (INR only) ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="forecast-kpis">
                    <Kpi
                        label="Total expected (12m, INR)"
                        value={formatINR(hl.total_expected_12m)}
                        icon={<Coins size={11} />}
                        tone="emerald"
                        testid="forecast-kpi-total"
                    />
                    <Kpi
                        label="AMC recurring (INR)"
                        value={formatINR(hl.amc_recurring_total)}
                        icon={<Repeat size={11} />}
                        tone="indigo"
                        sub="Rolling relationships"
                        testid="forecast-kpi-amc"
                    />
                    <Kpi
                        label="One-time invoice (INR)"
                        value={formatINR(hl.one_time_total)}
                        icon={<Receipt size={11} />}
                        tone="slate"
                        sub="Open invoices + PO milestones"
                        testid="forecast-kpi-onetime"
                    />
                    <Kpi
                        label="Overdue outstanding (INR)"
                        value={formatINR(hl.overdue_outstanding)}
                        icon={<Warning size={11} weight="fill" />}
                        tone="rose"
                        sub="Past-due invoices (context)"
                        testid="forecast-kpi-overdue"
                    />
                </div>

                {/* ── Foreign currency totals (native units, no FX) ── */}
                {foreignCurrencies.length > 0 && (
                    <ForeignCurrencyPanel
                        totals={foreignTotals}
                        overdue={hl.overdue_foreign_currency || {}}
                    />
                )}

                {/* ── Monthly chart (INR only) ── */}
                <div className="reedos-card p-5" data-testid="forecast-monthly-chart">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-1.5">
                                <ChartLineUp size={11} /> Monthly expected inflow · INR
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                12 months · stacked by stream · INR only
                            </div>
                        </div>
                        <div className="flex items-center gap-3 text-[10px]">
                            <Legend tone="indigo" label="AMC recurring" />
                            <Legend tone="emerald" label="One-time" />
                        </div>
                    </div>
                    {allZero ? (
                        <EmptyState
                            icon={<ChartLineUp size={36} weight="thin" />}
                            title="No projected inflow"
                            description="No active AMC schedules or open one-time invoices fall within the next 12 months."
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <div className="flex gap-2 items-end pt-4" style={{ minWidth: "1080px" }}>
                                {data.months.map((m, i) => {
                                    const amcH = (m.amc_recurring / maxBucket) * 200;
                                    const otH = (m.one_time / maxBucket) * 200;
                                    return (
                                        <div key={i} className="flex flex-col items-center gap-1 flex-1 min-w-[72px]" data-testid={`forecast-month-${i}`}>
                                            <div className="text-[10px] font-mono text-slate-600 leading-tight text-center min-h-[14px]">
                                                {m.total > 0 ? formatINRCompact(m.total) : ""}
                                            </div>
                                            <div className="flex flex-col items-center justify-end h-[200px] w-full gap-0.5">
                                                {otH > 0 && (
                                                    <div
                                                        className="w-full bg-emerald-500/80 rounded-t"
                                                        style={{ height: `${otH}px` }}
                                                        title={`One-time: ${formatINR(m.one_time)}`}
                                                    />
                                                )}
                                                {amcH > 0 && (
                                                    <div
                                                        className={classJoin("w-full bg-indigo-500/80", otH === 0 ? "rounded-t" : "")}
                                                        style={{ height: `${amcH}px` }}
                                                        title={`AMC: ${formatINR(m.amc_recurring)}`}
                                                    />
                                                )}
                                                {(amcH === 0 && otH === 0) && <div className="w-full h-px bg-slate-200" />}
                                            </div>
                                            <div className="text-[10px] font-semibold text-slate-700 text-center">{m.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Supporting table with per-currency drill-down ── */}
                <div className="reedos-card overflow-hidden" data-testid="forecast-monthly-table">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div>
                            <div className="reedos-eyebrow">Monthly breakdown</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                INR + currency mix per month · click <strong>Reconcile</strong> to trace any row
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/50">
                                <tr className="text-left">
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Month</th>
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">AMC recurring (INR)</th>
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">One-time (INR)</th>
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">Total (INR)</th>
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Foreign currencies (native)</th>
                                    <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">Reconcile</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.months.map((m) => (
                                    <tr key={m.iso_start} className="border-t border-slate-100 reedos-row-hover" data-testid={`forecast-row-${m.iso_start}`}>
                                        <td className="px-4 py-2 text-xs font-semibold text-slate-800">{m.label}</td>
                                        <td className="px-4 py-2 text-xs text-right reedos-data text-indigo-700">
                                            {m.amc_recurring > 0 ? formatINR(m.amc_recurring) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-right reedos-data text-emerald-700">
                                            {m.one_time > 0 ? formatINR(m.one_time) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-right reedos-data font-bold text-slate-900">
                                            {m.total > 0 ? formatINR(m.total) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-700" data-testid={`forecast-row-foreign-${m.iso_start}`}>
                                            <ForeignCellSummary byCcy={m.by_currency || {}} />
                                        </td>
                                        <td className="px-4 py-2 text-xs text-right">
                                            <ReconcileButton
                                                month={m.iso_start.slice(0, 7)}
                                                disabled={m.total === 0 && Object.keys(m.by_currency || {}).filter((c) => c !== "INR").length === 0}
                                                onClick={() => setReconcileMonth(m.iso_start.slice(0, 7))}
                                                testid={`forecast-reconcile-${m.iso_start}`}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-slate-50/80 border-t-2 border-slate-200">
                                <tr>
                                    <td className="px-4 py-2.5 text-xs font-bold text-slate-800">12-month total (INR)</td>
                                    <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-indigo-800" data-testid="forecast-total-amc">
                                        {formatINR(hl.amc_recurring_total)}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-emerald-800" data-testid="forecast-total-onetime">
                                        {formatINR(hl.one_time_total)}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-slate-900" data-testid="forecast-total-grand">
                                        {formatINR(hl.total_expected_12m)}
                                    </td>
                                    <td colSpan={2} className="px-4 py-2.5 text-[10px] text-right text-slate-500">
                                        Foreign-currency 12m totals shown in the panel above
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* ── Methodology footer ── */}
                <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4" data-testid="forecast-methodology">
                    <strong className="text-slate-700">Methodology:</strong> All amounts shown{" "}
                    <strong className="text-slate-700">net of GST</strong> — GST is a pass-through liability
                    and is excluded from the projection. AMC schedules are treated as rolling commercial
                    relationships, projected across the full 12-month window using <strong>calendar-month
                    arithmetic</strong> (Half-Yearly = +6 months, Quarterly = +3, Monthly = +1) unless the
                    schedule is <span className="font-mono">Paused</span>, <span className="font-mono">Expired</span>,
                    or archived.{" "}
                    <strong className="text-slate-700">AMC-linked invoices bucket by{" "}
                    <span className="font-mono">billing_end_date</span></strong>{" "}
                    (the AMC cycle date) so the forecast reconciles 1:1 with the AMC pipeline view.
                    Non-AMC invoices bucket by <span className="font-mono">due_date</span>. PO unbilled
                    milestones bucket by <span className="font-mono">expected_date</span> at their net
                    (ex-GST) value.{" "}
                    <strong className="text-slate-700">Archive cascade:</strong> open invoices, schedule
                    projections and milestones tied to an archived client, AMC schedule or PO are
                    automatically excluded from the forecast (financial records themselves remain
                    immutable).{" "}
                    <strong className="text-slate-700">Overdue, Aging Report and Collections priority all
                    use{" "}<span className="font-mono">due_date</span></strong>{" "}
                    — payment-discipline timing is intentionally separate from billing-cycle visibility.{" "}
                    <strong className="text-slate-700">Foreign-currency policy:</strong> values stay in
                    native currency — no FX conversion is applied (Multi-Currency Phase 2 deferred per
                    operator directive).
                </div>
            </div>

            {/* ── Reconciliation drill-down dialog ── */}
            <ReconcileDialog
                month={reconcileMonth}
                onClose={() => setReconcileMonth(null)}
            />
        </div>
    );
}


function ForeignCurrencyPanel({ totals, overdue }) {
    const codes = Object.keys(totals).sort();
    return (
        <div className="reedos-card p-5" data-testid="forecast-foreign-panel">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <div className="reedos-eyebrow flex items-center gap-1.5">
                        <CurrencyDollar size={11} /> Foreign currency totals · next 12 months
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        Reported in native currency · no FX conversion
                    </div>
                </div>
                <div className="text-[10px] text-slate-400 italic">
                    {codes.length} non-INR {codes.length === 1 ? "currency" : "currencies"}
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {codes.map((ccy) => {
                    const t = totals[ccy];
                    return (
                        <div
                            key={ccy}
                            className="reedos-card p-3 border border-slate-200 bg-amber-50/30"
                            data-testid={`forecast-foreign-tile-${ccy}`}
                        >
                            <div className="reedos-eyebrow text-[10px] text-amber-700 flex items-center gap-1.5">
                                {ccy}
                                {overdue[ccy] ? (
                                    <span className="text-[9px] uppercase font-bold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                                        Overdue {formatCcy(overdue[ccy], ccy)}
                                    </span>
                                ) : null}
                            </div>
                            <div className="text-xl font-display font-bold reedos-data text-slate-900 mt-1 leading-none">
                                {formatCcy(t.total, ccy)}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1.5 leading-tight space-y-0.5">
                                <div>AMC: <span className="reedos-data text-indigo-700">{formatCcy(t.amc_recurring, ccy)}</span></div>
                                <div>One-time: <span className="reedos-data text-emerald-700">{formatCcy(t.one_time, ccy)}</span></div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function ForeignCellSummary({ byCcy }) {
    const codes = Object.keys(byCcy || {}).filter((c) => c !== "INR").sort();
    if (codes.length === 0) return <span className="text-slate-300">—</span>;
    return (
        <div className="flex flex-wrap gap-1.5">
            {codes.map((ccy) => (
                <span
                    key={ccy}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-100 text-amber-800"
                    title={`${ccy} (native currency — no FX conversion applied)`}
                >
                    {formatCcy(byCcy[ccy].total, ccy)}
                </span>
            ))}
        </div>
    );
}


function ReconcileButton({ month, disabled, onClick, testid }) {
    return (
        <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={onClick}
            className="gap-1 text-slate-600 hover:text-slate-900 text-[11px]"
            data-testid={testid}
            title={disabled ? "No contributing rows in this month" : `Show contributing rows for ${month}`}
        >
            <MagnifyingGlass size={11} /> Reconcile
        </Button>
    );
}


function ReconcileDialog({ month, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!month) {
            setData(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const { data: d } = await api.get(`/billing/reports/cashflow-forecast/reconcile?month=${month}`);
                if (!cancelled) setData(d);
            } catch (err) {
                if (!cancelled) {
                    setError(formatApiError(err, "Failed to load reconciliation"));
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [month]);

    return (
        <Dialog open={!!month} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
                data-testid="forecast-reconcile-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <MagnifyingGlass size={16} weight="duotone" className="text-indigo-600" />
                        Reconcile · {data?.month_label || month}
                        <span className="text-[11px] font-normal text-slate-500 ml-2">
                            {data?.row_count != null && `${data.row_count} contributing ${data.row_count === 1 ? "row" : "rows"}`}
                        </span>
                    </DialogTitle>
                </DialogHeader>
                <div className="overflow-auto flex-1 -mx-6 px-6 space-y-4">
                    {loading && <div className="text-sm text-slate-500 py-8">Loading reconciliation…</div>}
                    {error && (
                        <div className="text-sm text-rose-600 py-6 px-3 rounded bg-rose-50 border border-rose-100">
                            {error}
                        </div>
                    )}
                    {!loading && !error && data && data.row_count === 0 && (
                        <div className="text-sm text-slate-500 py-8 text-center">
                            No contributing rows for {data.month_label}.
                        </div>
                    )}
                    {!loading && !error && data && data.row_count > 0 && (
                        <>
                            {/* Per-currency subtotals */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="reconcile-subtotals">
                                {Object.entries(data.by_currency_totals).sort().map(([ccy, t]) => (
                                    <div
                                        key={ccy}
                                        className={classJoin(
                                            "rounded border px-3 py-2",
                                            ccy === "INR" ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50/40",
                                        )}
                                        data-testid={`reconcile-subtotal-${ccy}`}
                                    >
                                        <div className="reedos-eyebrow text-[10px]">{ccy}</div>
                                        <div className="text-sm reedos-data font-bold text-slate-900">{formatCcy(t.total, ccy)}</div>
                                        <div className="text-[10px] text-slate-500">
                                            {t.count} {t.count === 1 ? "row" : "rows"}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Row table */}
                            <div className="reedos-card overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50/70 text-left">
                                        <tr>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px]">Source</th>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px]">Stream</th>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px]">Reference</th>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px]">Client</th>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px]">Date</th>
                                            <th className="px-3 py-2 reedos-eyebrow text-[10px] text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody data-testid="reconcile-rows">
                                        {data.rows.map((r, idx) => (
                                            <tr key={`${r.source}-${r.id}-${idx}`} className="border-t border-slate-100 hover:bg-slate-50/40">
                                                <td className="px-3 py-1.5">
                                                    <SourceBadge source={r.source} />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <StreamPill stream={r.stream} />
                                                </td>
                                                <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">
                                                    {r.label}
                                                    {r.source === "amc_schedule" && r.billing_frequency && (
                                                        <span className="ml-1.5 text-[9px] text-slate-400">({r.billing_frequency})</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-slate-700">{r.client_name || "—"}</td>
                                                <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">
                                                    {(r.source_date || "").slice(0, 10)}
                                                    {r.source === "amc_schedule" && r.schedule_next_due_anchor && (
                                                        <span className="block text-[9px] text-slate-400">
                                                            anchor: {(r.schedule_next_due_anchor || "").slice(0, 10)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right reedos-data font-semibold">
                                                    <span className={r.currency === "INR" ? "text-slate-900" : "text-amber-800"}>
                                                        {formatCcy(r.amount, r.currency)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="text-[10px] text-slate-500 italic px-1">
                                Bucket source dates:{" "}
                                <code className="font-mono">billing_end_date</code> for AMC-linked invoices
                                (so the forecast aligns with the AMC pipeline cycle, Iter 51.5),{" "}
                                <code className="font-mono">due_date</code> for non-AMC invoices,{" "}
                                <code className="font-mono">projected_next_due</code> for AMC schedules
                                (computed from anchor + calendar-month stride),{" "}
                                <code className="font-mono">expected_date</code> for PO milestones.
                                Foreign-currency rows shown in native units (no FX conversion). Archived
                                clients / schedules / POs are cascade-excluded.
                            </div>
                        </>
                    )}
                </div>
                <div className="flex justify-end pt-3 border-t border-slate-100 mt-2">
                    <Button variant="outline" size="sm" onClick={onClose} className="gap-1" data-testid="reconcile-close-btn">
                        <X size={12} /> Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function SourceBadge({ source }) {
    const map = {
        amc_schedule: { label: "AMC", cls: "bg-indigo-100 text-indigo-800" },
        invoice: { label: "Invoice", cls: "bg-slate-100 text-slate-800" },
        po_milestone: { label: "Milestone", cls: "bg-emerald-100 text-emerald-800" },
    };
    const m = map[source] || { label: source, cls: "bg-slate-100 text-slate-700" };
    return (
        <span className={classJoin("inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider", m.cls)}>
            {m.label}
        </span>
    );
}


function StreamPill({ stream }) {
    const map = {
        amc_recurring: { label: "AMC recurring", cls: "text-indigo-700" },
        one_time: { label: "One-time", cls: "text-emerald-700" },
    };
    const m = map[stream] || { label: stream, cls: "text-slate-600" };
    return <span className={classJoin("text-[10px] font-semibold", m.cls)}>{m.label}</span>;
}


function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        indigo: "text-indigo-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}


function Legend({ tone, label }) {
    const colors = {
        emerald: "bg-emerald-500/80",
        indigo: "bg-indigo-500/80",
    };
    return (
        <span className="inline-flex items-center gap-1.5 text-slate-500">
            <span className={classJoin("w-2.5 h-2.5 rounded-sm", colors[tone])} />
            {label}
        </span>
    );
}
